import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import './PolicyMarketSideMenu.css'
import {useDispatch, useSelector, useStore} from "react-redux";
import {
  selectCurrentPlayer,
  selectGameResults,
  selectGameType,
  selectIsGameStarted,
  selectNextInitiativePlayerId,
  selectPhase,
  selectPlayers,
  selectPolicyMarket,
  selectTurnOrder
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {getActingPlayer} from "../../../utils/players/getActingPlayer";
import {selectUserId} from "../../../redux/reducers/privateStateReducer/selectors";
import {SPECTATOR_ID} from "../../../constants/players";
import {CONTROLS_COLOR} from "../../../styles/constants";
import {LoadedImage} from "../../EmblaCarousel/FactionsCarousel";
import {getPolicyImagePath} from "../../../utils/policy/getPolicyImagePath";
import {StatWell} from "../../gameUi/StatWell";
import {calcPolicyPrice} from "../../../utils/policy/calcPolicyPrice";
import {
  addNewPolicyToPolicyMarket,
  buyPolicy,
  showIsolationismModal,
  showToast,
  showToastWaitOtherPlayerTurn
} from "../../../redux/actions";
import {getRandomPolicyForMarket} from "../../../utils/policy/getRandomPolicyForMarket";
import PolicyCardOverlay from "../../PolicyCardOverlay/PolicyCardOverlay";
import {useIsCurrentUserTurn} from "../../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {useTutorialUiFlags} from "../../../utils/tutorial/useTutorialUiFlags";
import {checkTutorialActionWouldApply} from "../../../utils/tutorial/tutorialActionGate";
import {checkIfTurnInputBlocked} from "../../../utils/gameState/turn/turnInputLock";
import {PHASES} from "../../../constants/phases";
import PolicyMarketClearButton from "../../Footer/PolicyMarketClearButton/PolicyMarketClearButton";
import {getPlayerNextPolicy} from "../../../utils/policy/getPlayerNextPolicy";
import {addClassNameIfTrue} from "../../../utils/other/addClassNameIfTrue";
import {POLICIES} from "../../../constants/policies";
import {getPlayerById} from "../../../utils/players/getPlayerById";
import {isPlayerPolicy} from "../../../utils/factions/isPlayerPolicy";
import {isPlayerNextPolicy} from "../../../utils/factions/isPlayerNextPolicy";
import {SIDE_MENU_IDS, useSideMenuStack} from "../../../contexts/SideMenuStackContext";
import {useSideMenuEscape} from "../../../hooks/input/useSideMenuEscape";
import {usePolicyMarketSideMenu} from "../../../contexts/PolicyMarketSideMenuContext";
import {usePlayersListSideMenu} from "../../../contexts/PlayersListSideMenuContext";
import {useDesktopLayout} from "../../../hooks/visuals/useWideScreenLayout";
import {useReplayControls} from "../../../contexts/ReplayControlsContext";
import {selectTurnDisplayedPlayer} from "../../../utils/gameState/supply/parallelSupplySelectors";
import {selectConstructionPlanStatsView} from "../../../utils/gameState/construction/planning/buildConstructionPlanningViewState";
import {POLICY_CONFIGS_IMPLEMENTED} from "../../../configs/policies";
import {SIDE_MENU_TAB_ORDER, SideMenuTab} from "../SideMenuTabs/SideMenuTabs";
import {getPlayerColorStyle} from "../../../utils/gameUi/getPlayerColorStyle";
import HeaderDropdown, {HEADER_DROPDOWN_ANCHORS} from "../HeaderDropdown/HeaderDropdown";
import {useGameUiPortalTarget} from "../../gameUi/GameUiPortal";

const PolicyMarketSideMenu = ({isSpectatorMode = false}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const tutorialUiFlags = useTutorialUiFlags();
  const isGameStarted = useSelector(selectIsGameStarted);
  const gameType = useSelector(selectGameType);
  const userId = useSelector(selectUserId);
  const gameResults = useSelector(selectGameResults);
  const phase = useSelector(selectPhase);
  const players = useSelector(selectPlayers);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const policyMarket = useSelector(selectPolicyMarket);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const nextInitiativePlayerId = useSelector(selectNextInitiativePlayerId);
  const turnOrder = useSelector(selectTurnOrder);
  // Игрок хода для зрителя: текущий игрок, а в одновременной фазе снабжения
  // сетевой игры — тот же, что на кнопке завершения хода (первый еще не
  // закончивший), а не застывший на месте якорь хода
  const turnDisplayedPlayer = useSelector(selectTurnDisplayedPlayer);
  // Планирование в чужой ход строительства: монеты с вычетом плана — те же,
  // что в шапке и на кнопке рынка, по ним цены и «не по карману»
  const planStatsView = useSelector(selectConstructionPlanStatsView);

  const {topSideMenuId} = useSideMenuStack();
  const {isPanelsVisible} = useReplayControls();

  // И в REPLAY, и в локальном режиме зрителя кнопка видна по умолчанию и
  // переключается двойным кликом по CompletePhaseButton (isPanelsVisible из
  // ReplayControlsContext) — в обычной игре isSpectatorMode всегда false,
  // и кнопка скрытой быть не может независимо от isPanelsVisible
  // На десктопе ярлыков у края нет вовсе: рынок открывает кнопка рынка в
  // шапке (HeaderMineZone). На телефоне до шапки тянуться
  // неудобно — там ярлыки остаются
  const isDesktopLayout = useDesktopLayout();
  const isButtonHidden = (isSpectatorMode && !isPanelsVisible) || isDesktopLayout;

  // Открытие панели и раскрытая карточка — общие с кнопками рынка в шапке
  const {
    isMenuOpen,
    selectedPolicy,
    setSelectedPolicy,
    closeMenu: handleCloseMenu,
    toggleMarket: handleOpenMarket,
  } = usePolicyMarketSideMenu();
  const {openCard: openPolicyCard} = usePlayersListSideMenu();
  const [isFading, setIsFading] = useState(false);
  // Карточка политики — поверх всего экрана матча, а не внутри списка рынка
  const portalTarget = useGameUiPortalTarget();

  const [selectedPolicyIndex, setSelectedPolicyIndex] = useState(null);
  const [isMarketPolicySelected, setIsMarketPolicySelected] = useState(false);

  useEffect(() => {
    if (!isMenuOpen) {
      const timeoutId = setTimeout(() => {
        setSelectedPolicy(null);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [isMenuOpen, setSelectedPolicy]);

  useEffect(() => {
    if (!selectedPolicy) {
      setIsMarketPolicySelected(false);
      setSelectedPolicyIndex(null);
    }
  }, [selectedPolicy]);

  // Панель ушла под другую (переключение НЕ закрывает меню): открытая
  // карточка политики рынка иначе остается смонтированной невидимой — и,
  // среди прочего, ее кнопка «Скрыть» перехватывает первый querySelector
  // стрелки обучения
  useEffect(() => {
    if (topSideMenuId !== SIDE_MENU_IDS.POLICY_MARKET) {
      setSelectedPolicy(null);
    }
  }, [topSideMenuId, setSelectedPolicy]);

  useSideMenuEscape(SIDE_MENU_IDS.POLICY_MARKET, isMenuOpen, handleCloseMenu);

  const actingPlayer = getActingPlayer(gameType, userId, players, currentPlayer);
  if (!actingPlayer) {
    return null;
  }

  const displayedPlayer = actingPlayer.id === SPECTATOR_ID ? turnDisplayedPlayer : actingPlayer;
  if (!displayedPlayer) {
    return null;
  }
  const availableCoins = planStatsView && planStatsView.player.id === displayedPlayer.id
    ? planStatsView.player.coins
    : displayedPlayer.coins;

  const handlePurchase = () => {
    if (!isGameStarted) {
      dispatch(showToast('Нельзя покупать политику до начала игры!', 'warning', 3000));
      return;
    }

    if (gameResults) {
      dispatch(showToast('Нельзя покупать политику до начала игры!', 'warning', 3000));
      return;
    }

    if (phase !== PHASES.CONSTRUCTION) {
      dispatch(showToast('Политику можно купить только в фазе Строительства!', 'warning', 3000));
      return;
    }

    if (!isCurrentUserTurn) {
      dispatch(showToastWaitOtherPlayerTurn(currentPlayer));
      return;
    }

    const price = calcPolicyPrice(selectedPolicyIndex, currentPlayer);

    if (currentPlayer.coins < price) {
      dispatch(showToast('У вас недостаточно монет для покупки этой политики!', 'warning', 3000));
      return;
    }

    if (currentPlayer.policy
      && currentPlayer.policy.id === selectedPolicy.id
    ) {
      dispatch(showToast('Нельзя купить политику, которая у вас уже активна!', 'warning', 3000));
      return;
    }

    if (currentPlayer.nextPolicy) {
      dispatch(showToast('Вы уже купили новую политику в этом ходу!', 'warning', 3000));
      return;
    }

    // Гейт обучения: заблокированная покупка не должна звучать —
    // диспатчим (гейт покажет подсказку) и выходим
    const buyPolicyAction = buyPolicy(selectedPolicyIndex);
    if (!checkTutorialActionWouldApply(store.getState(), buyPolicyAction)) {
      dispatch(buyPolicyAction);
      return;
    }
    // Время хода вышло (см. turnInputLock): покупку отбросит страж блокировки
    // — так же без звука и, главное, без модалки Изоляционизма на некупленную
    // политику
    if (checkIfTurnInputBlocked(store.getState())) {
      dispatch(buyPolicyAction);
      return;
    }

    REGISTRY_SINGLETON.getSoundManager().playBuyPolicy(price);

    dispatch(buyPolicyAction);
    dispatch(addNewPolicyToPolicyMarket(getRandomPolicyForMarket()))
    setSelectedPolicy(null);

    const currentInitiativeOwner = getPlayerById(players, turnOrder[0]);

    if (selectedPolicy.id === POLICIES.ISOLATIONISM
      && !nextInitiativePlayerId
      && currentInitiativeOwner.id !== currentPlayer.id
      && !isPlayerPolicy(currentInitiativeOwner, POLICIES.ISOLATIONISM)
      && !isPlayerNextPolicy(currentInitiativeOwner, POLICIES.ISOLATIONISM)
    ) {
      dispatch(showIsolationismModal());
    }
  };

  const playCardSound = () => {
    REGISTRY_SINGLETON.getSoundManager().playCardOpen();
  }

  const displayedPlayerPolicy = displayedPlayer.policy;
  const displayedPlayerNextPolicy = getPlayerNextPolicy(displayedPlayer);

  // Ярлык своей политики открывает только ее карточку — поверх экрана, без
  // рынка (как своя политика в шапке на десктопе и иконка политики на кнопке
  // завершения фазы); рынок — свой ярлык ниже
  const handleOpenOwnPolicy = () => {
    playCardSound();
    openPolicyCard(displayedPlayer, displayedPlayerPolicy);
  };

  // Главы обучения без темы политик: рынок скрыт целиком, вместе с кнопкой
  // открытия — тема появляется только в главе 3
  const isOwnPolicyShownOnTab = !isSpectatorMode && Boolean(displayedPlayerPolicy);

  if (tutorialUiFlags.hidePolicyMarket) {
    return null;
  }

  return (
    <>
      {!isButtonHidden && (
        <SideMenuTab>
          {/* Ярлык рынка — нейтральный базальт с мини-сеткой политик. Иконка
              здания правительства — только пока у игрока нет своей политики:
              купленная выносится отдельным ярлыком над рынком */}
          {/* Ярлык подсвечен, пока рынок открыт, даже если поверх лежит
              другой список */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenMarket();
            }}
            className={`gui-plate gui-plate_bar gui-shape_right side-menu-tab policy-side-menu-tab ${
              addClassNameIfTrue(isMenuOpen, 'side-menu-tab-opened')
            }`}
            style={{order: SIDE_MENU_TAB_ORDER.POLICY_MARKET}}
          >
            {!isOwnPolicyShownOnTab && (
              <div className={'side-menu_active-player'}>
                <img
                  className={'policy-market_tab-cards-icon'}
                  src={'/img/icons/governance.png'}
                  alt={'Рынок политик'}
                />
              </div>
            )}

            {policyMarket && policyMarket.length > 0 && (
              <div className={'policy-sime-menu-tab_market-mini-container'}>
                {policyMarket.map((policy, index) => {
                  return (<img
                    key={index}
                    className={`policy-sime-menu-tab_market-mini-policy`}
                    src={getPolicyImagePath(policy.id)}
                    alt={policy.name}
                    title={policy.name}
                  />);
                })}
              </div>
            )}
          </button>
        </SideMenuTab>
      )}

      {/* Своя политика — отдельный ярлык между карточкой игрока и рынком. В
      реплее и режиме зрителя избыточна — та же иконка уже показана на
      CompletePhaseButton */}
      {!isButtonHidden && isOwnPolicyShownOnTab && (
        <SideMenuTab>
          {/* Обертка без clip-path: плита ярлыка обрезает все внутри себя по
              своему контуру, а купленная на следующий раунд политика
              выступает за ее правый нижний угол. Порядок в колонке и цвет
              игрока — на обертке */}
          <span
            className={'policy-market_own-policy-tab'}
            style={{
              ...getPlayerColorStyle(actingPlayer.color),
              order: SIDE_MENU_TAB_ORDER.OWN_POLICY,
            }}
          >
            {/* «Мое» — эмаль цвета игрока */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenOwnPolicy();
              }}
              className={'gui-plate gui-plate_mine gui-shape_right side-menu-tab policy-side-menu-tab policy-side-menu-tab_own-policy'}
              title={displayedPlayerNextPolicy
                ? `${displayedPlayerPolicy.name} → ${displayedPlayerNextPolicy.name}`
                : displayedPlayerPolicy.name}
            >
              <LoadedImage
                className={`policy-market_tab-policy-img`}
                src={getPolicyImagePath(displayedPlayerPolicy.id)}
                alt={displayedPlayerPolicy.name}
              />
            </button>
            {/* Купленная на следующий раунд — поверх ярлыка, за его правым
                нижним углом (как значок бота за портрет), в рамке цвета
                игрока: иначе сливается с картинкой текущей. Слева от нее, по
                середине ее высоты, — стрелка «текущая → следующая», как между
                политиками в шапке на десктопе. Нажатие проходит сквозь них
                на ярлык */}
            {displayedPlayerNextPolicy && (
              <>
                <img
                  className={'policy-market_tab-policy-arrow'}
                  src={'/img/icons/up white.png'}
                  alt={''}
                  aria-hidden={true}
                />
                <span className={'policy-market_tab-policy-next'}>
                  <LoadedImage
                    className={'policy-market_tab-policy-img policy-market_tab-policy-img_next'}
                    src={getPolicyImagePath(displayedPlayerNextPolicy.id)}
                    alt={displayedPlayerNextPolicy.name}
                  />
                </span>
              </>
            )}
          </span>
        </SideMenuTab>
      )}

      {/* Рынок выпадает вниз от кнопки рынка в шапке, а на
          телефоне выезжает снизу листом во всю ширину (свайп вниз закрывает).
          Своей политики здесь нет: она в шапке (на телефоне — на ярлыке), ее
          карточка открывается из списка игроков и с кнопки хода */}
      <HeaderDropdown
        isOpen={isMenuOpen}
        onClose={handleCloseMenu}
        anchor={HEADER_DROPDOWN_ANCHORS.MARKET}
        isFocused={topSideMenuId === SIDE_MENU_IDS.POLICY_MARKET}
        title={(
          // Перед названием — иконка рынка (здание правительства), как на
          // кнопке рынка в шапке и на ярлыке у края экрана
          <span className={'policy-market_title'}>
            <img className={'policy-market_title-icon'} src={'/img/icons/governance.png'} alt={''}/>
            {'Рынок политик'}
          </span>
        )}
        isSheetOnPhone
        className={'policy-market'}
      >
        <div className={`policy-market_container ${isFading ? 'policy-market_container_fading' : ''}`}>
          {policyMarket && policyMarket.map((policy, index) => {
            let policyColor = policy.color;
            if(!policyColor) {
              policyColor = POLICY_CONFIGS_IMPLEMENTED.find(item => item.id === policy.id)?.color || CONTROLS_COLOR;
            }
            const price = calcPolicyPrice(index, displayedPlayer);
            return (
              <div
                key={index}
                // Не по карману — приглушена, но открывается: карточку можно
                // прочитать, а покупку отклонит handlePurchase с подсказкой.
                // При планировании — с учетом плана (availableCoins)
                className={`policy-market_policy ${
                  addClassNameIfTrue(price > availableCoins, 'policy-market_policy_unaffordable')
                }`}
                data-tutorial-select={policy.id}
                onClick={() => {
                  playCardSound();
                  setSelectedPolicy(policy);
                  setSelectedPolicyIndex(index);
                  setIsMarketPolicySelected(true);
                }}
              >
                {/* Цена — в углублении, как ресурсы в шапке */}
                <StatWell
                  icon={'/img/icons/coins.png'}
                  value={price}
                  title={'Цена в монетах'}
                  className={'policy-market_price'}
                />

                {/* Политика — плита базальта: картинка со срезанными углами,
                    название, справа — эмалевая метка цвета категории */}
                <div className={'gui-plate gui-plate_bar gui-shape_s policy-market_policy-info'}>
                  <LoadedImage
                    className={'policy-market_policy-img'}
                    src={getPolicyImagePath(policy.id)}
                    alt={policy.name}
                  />

                  <div className={'policy-market_policy-info_name'}>
                    {policy.name}
                  </div>

                  <span
                    className={'policy-market_category-mark'}
                    style={{backgroundColor: policyColor}}
                    aria-hidden={true}
                  />
                </div>
              </div>
            )
          })}
        </div>

        <PolicyMarketClearButton
          onClear={() => {
            setIsFading(true);
            setTimeout(() => setIsFading(false), 0);
          }}
        />

        {createPortal(
          <PolicyCardOverlay
            selectedPolicy={selectedPolicy}
            selectedPolicyIndex={selectedPolicyIndex}
            player={displayedPlayer}
            handlePurchase={handlePurchase}
            isMarketPolicySelected={isMarketPolicySelected}
            onClose={() => setSelectedPolicy(null)}
          />,
          portalTarget
        )}
      </HeaderDropdown>
    </>
  );
};

export default PolicyMarketSideMenu;
